// Platform settings page for configuring individual platform connections
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageHeader } from '@/components/ui/page-header';
import { Toaster } from '@/components/ui/toaster';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Settings, 
  Linkedin, 
  X, 
  Facebook, 
  Instagram, 
  Youtube,
  Globe,
  PenSquare,
  Link,
  Cog
} from 'lucide-react';
import PlatformConnectionCard from './PlatformConnectionCard';
import PlatformAutomationConfig from './PlatformAutomationConfig';
import usePlatformSettings from './hooks/usePlatformSettings';

// Custom TikTok icon component
const TikTokIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

// Function to get the appropriate icon based on platform key
const getPlatformIcon = (platformKey: string, size: 'sm' | 'md' | 'lg' = 'md') => {
  const sizeClass = size === 'sm' ? 'h-4 w-4' : size === 'lg' ? 'h-8 w-8' : 'h-5 w-5';
  
  switch(platformKey?.toLowerCase()) {
    case 'linkedin':
    case 'linkedin_article':
      return <Linkedin className={sizeClass} />;
    case 'twitter':
      return <X className={sizeClass} />;
    case 'facebook':
      return <Facebook className={sizeClass} />;
    case 'instagram':
      return <Instagram className={sizeClass} />;
    case 'youtube':
      return <Youtube className={sizeClass} />;
    case 'tiktok':
      return <TikTokIcon className={sizeClass} />;
    case 'wordpress':
      return <Globe className={sizeClass} />;
    case 'medium':
      return <PenSquare className={sizeClass} />;
    default:
      return <Globe className={sizeClass} />;
  }
};

export const PlatformSettings: React.FC = () => {
  const { id: brandId, platformId } = useParams<{ id: string; platformId: string }>();
  const [activeTab, setActiveTab] = useState('automation');

  const { 
    platforms, 
    platformSettings,
    platformConnectionStatus,
    loading,
    savePlatformSettings,
    updateConnectionStatus,
    testPlatformConnection,
    initiateAuthFlow,
    fetchLinkedInOrganizations,
    setLinkedInOrganization,
    linkedinOrganizations,
    loadingOrganizations,
    setFacebookPage
  } = usePlatformSettings(brandId);

  // Find the specific platform
  const platform = platforms.find(p => p.id === platformId);


  const handleSavePlatformSettings = async (platformId: string, settings: any) => {
    await savePlatformSettings(platformId, settings);
    // Optionally navigate back after saving
    // handleBack();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        <div className="relative container mx-auto p-6 max-w-6xl">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!platform) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        <div className="relative container mx-auto p-6 max-w-6xl">
          <div className="relative overflow-hidden bg-gradient-to-br from-white via-red-50/30 to-orange-50/40 rounded-2xl border border-red-200/50 shadow-sm p-8">
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            <div className="relative">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
                  <Settings className="h-8 w-8 text-red-600" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold bg-gradient-to-r from-red-700 via-orange-700 to-yellow-700 bg-clip-text text-transparent mb-2">
                    Platform Not Found
                  </h1>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    The requested platform could not be found
                  </p>
                </div>
              </div>
              <div className="mt-6">
                <p className="text-gray-600">
                  Use the sidebar navigation to return to connections.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const displayName = platform.platform_key === 'twitter' ? 'X' : 
                     platform.platform_key === 'linkedin_article' ? 'LinkedIn Articles' :
                     platform.name;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <PageHeader
        title={`${displayName} Agent Settings`}
        description={`Configure your ${displayName} connection and automation settings`}
        icon={getPlatformIcon(platform.platform_key)}
      />

      <div className="max-w-6xl">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8" defaultValue="automation">
          <div className="relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm p-1 sm:p-2 mx-auto max-w-2xl">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-50/30 via-transparent to-indigo-50/30" />
            <TabsList className="relative flex w-full flex-col items-stretch gap-1 rounded-lg bg-white/30 px-1 py-1 sm:w-auto sm:flex-row sm:items-center sm:justify-center sm:gap-2">
              <TabsTrigger 
                value="automation" 
                className="relative flex items-center justify-center gap-2 rounded-lg bg-white/50 px-4 py-2 text-sm font-medium text-slate-600 transition-all duration-200 hover:bg-white data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-pink-600 data-[state=active]:text-white data-[state=active]:shadow-md sm:px-6"
              >
                <Cog className="h-4 w-4" />
                Agent Configuration
              </TabsTrigger>
              <TabsTrigger 
                value="connection" 
                className="relative flex items-center justify-center gap-2 rounded-lg bg-white/50 px-4 py-2 text-sm font-medium text-slate-600 transition-all duration-200 hover:bg-white data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-md sm:px-6"
              >
                <Link className="h-4 w-4" />
                Connection
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="automation" className="space-y-6">
            <div className="relative overflow-hidden bg-gradient-to-br from-white via-purple-50/30 to-pink-50/30 rounded-2xl border border-purple-200/50 shadow-lg">
              <div className="absolute inset-0 bg-grid-pattern opacity-5" />
              <div className="relative p-6">
                
                <PlatformAutomationConfig
                  platform={platform}
                  platformSettings={platformSettings}
                  savePlatformSettings={handleSavePlatformSettings}
                  isConnected={true}
                  brandId={brandId!}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="connection" className="space-y-6">
            <div className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/30 rounded-2xl border border-blue-200/50 shadow-lg">
              <div className="absolute inset-0 bg-grid-pattern opacity-5" />
              <div className="relative p-1">
                <PlatformConnectionCard
                  platform={platform}
                  icon={getPlatformIcon(platform.platform_key)}
                  platformSettings={platformSettings}
                  platformConnectionStatus={platformConnectionStatus}
                  savePlatformSettings={handleSavePlatformSettings}
                  testPlatformConnection={testPlatformConnection}
                  updateConnectionStatus={updateConnectionStatus}
                  initiateAuthFlow={initiateAuthFlow}
                  fetchLinkedInOrganizations={fetchLinkedInOrganizations}
                  setLinkedInOrganization={setLinkedInOrganization}
                  linkedinOrganizations={linkedinOrganizations}
                  loadingOrganizations={loadingOrganizations}
                  setFacebookPage={setFacebookPage}
                  brandId={brandId!}
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      <Toaster />
    </div>
  );
};

export default PlatformSettings; 